import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Modal,
  Snackbar,
  tokens,
  Typography,
  LoadingIndicator,
  UtilityBillPastDueBalanceBanner,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMediaQuery } from "react-responsive";
import { CheckoutFooter } from "../molecules/CheckoutFooter";
import {
  Card,
  CardContent,
  CardHeader,
  CardSectionDivider,
} from "../molecules/Card";
import {
  SplatProspectResponse,
  SplatProspectUpdate,
} from "../../../../amplify/backend/function/OfferExpUsage/ts/public/types";
import { UnknownOdiModal } from "../molecules/UnknownOdiModal";
import { UtilityBillRequiredForFinalApprovalBanner } from "../molecules/UtilityBillRequiredForFinalApprovalBanner";
import { subscribeToAsyncRequest } from "utils/subscribeToAsyncRequest";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useCheckoutFooter } from "checkout/providers/CheckoutFooterContext";
import { HyperlinkButton } from "components/atoms/HyperlinkButton";
import { DocumentUpload } from "components/organisms/DocumentUpload";
import { UtilityInfo } from "components/organisms/UtilityInfo";
import { useUsageData, useUtilityData } from "utils/swrHooks";
import { Attachment, UtilityInfoState } from "utils/usageUtils";
import { utilityInfoFormSchema } from "providers/schema";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import {
  deleteAttachment,
  patchUsage,
  postCheckoutAttachment,
} from "services/prospect";
import { useAuthToken } from "hooks/useAuthToken";
import {
  uploadDocsOdis,
  useConfirmUtility,
  UtilityCheckListStep,
} from "checkout/hooks/useConfirmUtility";
import { useCheckoutAttachmentsNoType } from "checkout/hooks/useCheckoutSwr";
import { reloadMFE } from "utils/useNavigateHelper";
import { useCheckoutStepsData } from "checkout/api/checkoutSteps";
import { DocumentUploadWithModal } from "components/organisms/DocumentUploadWithModal";
import { ContractReviewBanners } from "components/molecules/ContractReviewBanners";
import {
  is12MonthActualUsageMethodMessage,
  isMobileOrManufacturedHomeMessage,
  isRateScheduleMismatchMessage,
  isTrustAgreementNameMatchMessage,
} from "utils/contractReviewUtils";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import { hasSubmittedToContractReview } from "constants/prospect";

const isRequestTooLongUploadError = (error: Error | null): boolean => {
  if (!error) {
    return false;
  }

  return error.message === "Request Too Long";
};

const getUtilityErrorModalCopy = (
  patchErrorMessage: Error | null,
  attachmentErrorMessage: Error | null
) => {
  if (patchErrorMessage) {
    return {
      title: "There was an issue saving utility information",
      body: "Our system is having trouble saving your utility information at the moment. Please refresh the page and try again.",
    };
  }

  if (isRequestTooLongUploadError(attachmentErrorMessage)) {
    return {
      title: "File size too large",
      body: "Please try uploading a smaller file.",
    };
  }

  return {
    title: "There was an issue uploading your files",
    body: "Our system is having trouble uploading your files at the moment. Please refresh the page and try again.",
  };
};

const ConfirmUtilityTaskView: React.FC = () => {
  const navigate = useNavigate();
  const {
    tasksLoading,
    prospectId,
    currentTask,
    proposalId,
    refreshTasks,
    refreshAndNavigateNext,
    contractReviewMessages,
  } = useCheckoutContext();
  const { utilityChecklistSteps = [], unknownOdis } = useConfirmUtility({
    proposalId,
    prospectId,
  });
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const [showUnknownOdisModal, setShowUnknownOdisModal] = useState(true);
  const authKey = useAuthToken("HYBRID")?.token || "no-token";
  const { userProfile } = useCheckoutStepsData(prospectId, proposalId, authKey);
  const isPartner = userProfile?.isPartner;

  const [editUtilityInfo, setEditUtilityInfo] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const successSnackbarMessage = useRef("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [isAttachmentsRefreshing, setIsAttachmentsRefreshing] = useState(false);
  const [originalUsage, setOriginalUsage] =
    useState<SplatProspectResponse | null>(null);
  const [patchErrorMessage, setPatchErrorMessage] = useState<null | Error>(
    null
  );
  const [attachmentErrorMessage, setAttachmentErrorMessage] =
    useState<null | Error>(null);
  const formUtils = useForm({
    resolver: zodResolver(utilityInfoFormSchema),
    defaultValues: {
      accountNumber: "",
      meterNumber: "",
      utilityCompany: "",
      rateSchedule: "",
    } as UtilityInfoState,
  });

  const toggleUtilityInfoEdit = () => setEditUtilityInfo((prev) => !prev);

  const { prospect: usage, error: usageError } = useUsageData(prospectId);

  const {
    data: utilitiesOptions,
    error: utilityError,
    isLoading: isUtilitiesLoading,
  } = useUtilityData(usage?.customerState as string, authKey);

  const {
    data: checkoutAttachments,
    error: allTypesDocumentAttachmentsError,
    isLoading: allTypesAttachmentsLoading,
    mutate: allTypesDocumentAttachmentsMutate,
  } = useCheckoutAttachmentsNoType(prospectId, proposalId);

  useEffect(() => {
    if (usage) {
      setOriginalUsage(usage);
      formUtils.reset({
        utilityBillDate: usage.utilityBillDate,
        utilityCompany: usage.utilityCompany,
        utilityServiceAddress: usage.utilityServiceAddress,
        rateSchedule: usage.rateSchedule,
        accountNumber: usage.accountNumber,
        meterNumber: usage.meterNumber,
      });
    }
  }, [usage, formUtils]);

  const handleDeleteUtilityBill = useCallback(
    async (file: Attachment & { documentId: string }) => {
      try {
        await deleteAttachment(authKey, prospectId, file.documentId);
        setIsAttachmentsRefreshing(true);
        await allTypesDocumentAttachmentsMutate();
        refreshTasks();
      } catch (err) {
        setAttachmentErrorMessage(err as Error);
        throw err;
      } finally {
        setIsAttachmentsRefreshing(false);
      }
    },
    [authKey, prospectId, allTypesDocumentAttachmentsMutate, refreshTasks]
  );

  const handleSaveUtilityInfoAndConfirm = useCallback(async () => {
    setIsLoading(true);
    const formValues = formUtils.getValues();

    if (
      formValues.accountNumber === originalUsage?.accountNumber &&
      formValues.meterNumber === originalUsage?.meterNumber &&
      formValues.utilityServiceAddress ===
        originalUsage?.utilityServiceAddress &&
      formValues.utilityBillDate === originalUsage?.utilityBillDate
    ) {
      return;
    }

    const prospectBody: Partial<SplatProspectUpdate> = {
      ...formValues,
    };
    if (
      formValues.utilityServiceAddress !== originalUsage?.utilityServiceAddress
    ) {
      prospectBody.partialOverrideAddressStandardization = true;
    }
    try {
      await patchUsage(authKey, prospectId, prospectBody).then((result) => {
        return subscribeToAsyncRequest(authKey, result.asyncRequestId);
      });

      successSnackbarMessage.current = "Utility Info updated successfully!";
      setShowSuccessSnackbar(true);
    } catch (err: any) {
      setPatchErrorMessage(err);
    } finally {
      setIsLoading(false);
      setEditUtilityInfo(false);
    }
  }, [originalUsage, prospectId, formUtils, authKey]);

  const handleAttachmentUploadSuccess = useCallback(
    (uploaded: Attachment[]) => {
      successSnackbarMessage.current = `Successfully uploaded ${
        uploaded.length
      } document${uploaded.length > 1 ? "s" : ""}!`;
      setShowSuccessSnackbar(true);
      setIsAttachmentsRefreshing(true);
      allTypesDocumentAttachmentsMutate().finally(() => {
        setIsAttachmentsRefreshing(false);
      });
      refreshTasks();
    },
    [allTypesDocumentAttachmentsMutate, refreshTasks]
  );

  const immediateUpload = useMemo(
    () => ({
      upload: (attachmentsToUpload: Attachment[]) =>
        Promise.all(
          attachmentsToUpload.map((attachment) =>
            postCheckoutAttachment(authKey, prospectId, proposalId, attachment)
          )
        ).then(() => undefined),
      onSuccess: handleAttachmentUploadSuccess,
      onError: setAttachmentErrorMessage,
      onLoadingChange: setIsDocumentLoading,
    }),
    [authKey, prospectId, proposalId, handleAttachmentUploadSuccess]
  );

  const { utilityBillPastDueBalanceFlowIsEnabled, hasUploadedUtilityBill } =
    useCheckoutContext();
  const { utilityPastDueBalance, utilityCompany } = usage ?? {};
  const hasUploadedUtilityBillFileWithPastDueBalance = Boolean(
    hasUploadedUtilityBill &&
      utilityPastDueBalance != null &&
      utilityPastDueBalance > 0 &&
      utilityCompany
  );

  const showUtilityBillRequiredBanner =
    utilityBillPastDueBalanceFlowIsEnabled && !hasUploadedUtilityBill;

  const showUtilityBillPastDueBalanceBanner =
    utilityBillPastDueBalanceFlowIsEnabled &&
    hasUploadedUtilityBillFileWithPastDueBalance;

  const isUtilityBillDeleteBlocked = hasSubmittedToContractReview(
    usage?.leadStage
  );

  const showFooter =
    !!currentTask &&
    !tasksLoading &&
    !usageError &&
    !utilityError &&
    !allTypesDocumentAttachmentsError;

  useCheckoutFooter(
    showFooter ? (
      <CheckoutFooter
        buttonText="Continue"
        isDisabled={isDocumentLoading}
        onClick={refreshAndNavigateNext}
        isLoading={isLoading}
      />
    ) : null,
    [showFooter, isDocumentLoading, refreshAndNavigateNext, isLoading]
  );

  if (!currentTask || tasksLoading) {
    return (
      <LoadingWrapper>
        <LoadingIndicator color="black" />
      </LoadingWrapper>
    );
  }

  if (usageError || utilityError || allTypesDocumentAttachmentsError) {
    return (
      <ErrorComponent
        context="UsagePage"
        action={
          usageError
            ? "Get Usage"
            : utilityError
            ? "Get Utility"
            : "Get Document Attachments"
        }
        error={usageError ?? utilityError ?? allTypesDocumentAttachmentsError}
      />
    );
  }

  const utilityErrorModalCopy = getUtilityErrorModalCopy(
    patchErrorMessage,
    attachmentErrorMessage
  );

  return (
    <>
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={4000}
        colorScheme="light"
        message={successSnackbarMessage.current}
        onClose={() => setShowSuccessSnackbar(false)}
        type="success"
      />
      {(!!patchErrorMessage || !!attachmentErrorMessage) && (
        <Modal
          testId="checkout-confirm-utility-error-modal"
          type="error"
          title={utilityErrorModalCopy.title}
          primaryButton={{
            text: "Refresh",
            onClick: () => reloadMFE(),
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => {
              setPatchErrorMessage(null);
              setAttachmentErrorMessage(null);
            },
          }}
          onClose={() => {
            setPatchErrorMessage(null);
            setAttachmentErrorMessage(null);
          }}
        >
          <Typography>{utilityErrorModalCopy.body}</Typography>
        </Modal>
      )}
      {unknownOdis && unknownOdis.length > 0 && showUnknownOdisModal && (
        <UnknownOdiModal
          odiNames={unknownOdis}
          isConfirmUtility={true}
          onClose={() => {
            setShowUnknownOdisModal(false);
          }}
        />
      )}
      <CardsContainer>
        {isUtilitiesLoading && <LoadingIndicator color="black" />}
        {!isUtilitiesLoading && !isPartner && (
          <>
            {editUtilityInfo ? (
              <UtilityInfo
                utilitiesOptions={utilitiesOptions}
                formUtils={formUtils}
                disabledFields={["utilityCompany"]}
                hiddenFields={["utilityCompany"]}
                actionFooter={{
                  primaryButtonText: "Save",
                  secondaryButtonText: "Cancel",
                  primaryButtonTestId:
                    "checkout-confirm-utility-save-utility-info",
                  secondaryButtonTestId:
                    "checkout-confirm-utility-cancel-utility-info",
                  primaryButtonFunction: handleSaveUtilityInfoAndConfirm,
                  secondaryButtonFunction: () => {
                    if (originalUsage) {
                      formUtils.reset({
                        utilityCompany: originalUsage.utilityCompany,
                        rateSchedule: originalUsage.rateSchedule,
                        accountNumber: originalUsage.accountNumber,
                        meterNumber: originalUsage.meterNumber,
                        utilityBillDate: originalUsage.utilityBillDate,
                        utilityServiceAddress:
                          originalUsage.utilityServiceAddress,
                      });
                    }
                    setEditUtilityInfo(false);
                  },
                  isPrimaryLoading: isLoading,
                }}
              />
            ) : (
              <Card>
                <CardHeader>
                  <Typography
                    size={tokens.FONT_SIZE_3}
                    color={tokens.BRAND_HEROBLUE}
                  >
                    Utility Information
                  </Typography>
                  <HyperlinkButton
                    onClick={toggleUtilityInfoEdit}
                    data-testid="checkout-confirm-utility-edit-utility-info"
                  >
                    Edit
                  </HyperlinkButton>
                </CardHeader>
                <CardSectionDivider />
                <CardContent>
                  <UtilityInfoFields isMobile={isMobile}>
                    <UtilityInfoField>
                      <Typography color={tokens.TINTS_OFF_WHITE_20}>
                        Account Number
                      </Typography>
                      <Typography>
                        {formUtils.getValues().accountNumber || "Not entered"}
                      </Typography>
                    </UtilityInfoField>
                    <UtilityInfoField>
                      <Typography color={tokens.TINTS_OFF_WHITE_20}>
                        Meter Number
                      </Typography>
                      <Typography>
                        {formUtils.getValues().meterNumber || "Not entered"}
                      </Typography>
                    </UtilityInfoField>
                    <UtilityInfoField>
                      <Typography color={tokens.TINTS_OFF_WHITE_20}>
                        Utility Service Address
                      </Typography>
                      <div>
                        <Typography>
                          {`${
                            formUtils.getValues().utilityServiceAddress ?? "-"
                          }`}
                        </Typography>
                      </div>
                    </UtilityInfoField>
                    <UtilityInfoField>
                      <Typography color={tokens.TINTS_OFF_WHITE_20}>
                        Date of latest utility bill
                      </Typography>
                      <div>
                        <Typography>
                          {`${formUtils.getValues().utilityBillDate ?? "-"}`}
                        </Typography>
                      </div>
                    </UtilityInfoField>
                  </UtilityInfoFields>
                </CardContent>
              </Card>
            )}
          </>
        )}
        {(allTypesAttachmentsLoading || isAttachmentsRefreshing) && (
          <LoadingIndicator color="black" />
        )}
        {utilityChecklistSteps
          .filter(
            (step: UtilityCheckListStep) =>
              !!(step && uploadDocsOdis.includes(step.name))
          )
          .sort((a, b) => a.order - b.order)
          .map((step) => (
            <DocumentUpload
              key={step.name}
              uploadedFiles={
                checkoutAttachments?.filter(
                  (doc) => doc.documentType === step?.documentType
                ) ?? []
              }
              immediateUpload={immediateUpload}
              documentType={step?.documentType ?? "Other"}
              description={step?.description ?? ""}
              title={step?.title ?? ""}
              onDeleteUploadedFile={
                step.documentType === "Utility Bill"
                  ? handleDeleteUtilityBill
                  : undefined
              }
              isDeleteBlocked={
                step.documentType === "Utility Bill"
                  ? isUtilityBillDeleteBlocked
                  : undefined
              }
              bannerContent={
                step.documentType === "Utility Bill" && (
                  <>
                    <ContractReviewBanners
                      messages={contractReviewMessages ?? []}
                      category="utility_bill_check"
                      filter={isRateScheduleMismatchMessage}
                      actionLink={{
                        text: "Update rate",
                        onClick: () =>
                          navigate(
                            `/prospect/${prospectId}/offer/confirm?openUsageSlideout=true`
                          ),
                      }}
                      sessionDismissScopeId={proposalId}
                    />
                    {!showUtilityBillRequiredBanner &&
                      !showUtilityBillPastDueBalanceBanner && (
                        <ContractReviewBanners
                          messages={contractReviewMessages ?? []}
                          category="utility_bill_check"
                          filter={(message) =>
                            !isRateScheduleMismatchMessage(message)
                          }
                          sessionDismissScopeId={proposalId}
                        />
                      )}
                    <ContractReviewBanners
                      messages={contractReviewMessages ?? []}
                      category="estimation_method_check"
                      filter={(message) =>
                        !is12MonthActualUsageMethodMessage(message)
                      }
                      sessionDismissScopeId={proposalId}
                    />
                    <ContractReviewBanners
                      messages={contractReviewMessages ?? []}
                      category="estimation_method_check"
                      filter={is12MonthActualUsageMethodMessage}
                      actionLink={{
                        text: "Update to 12 month rate",
                        onClick: () =>
                          navigate(
                            `/prospect/${prospectId}/offer/confirm?openUsageSlideout=true`
                          ),
                      }}
                      sessionDismissScopeId={proposalId}
                    />
                    {showUtilityBillRequiredBanner && (
                      <StyledUtilityBillRequiredForFinalApprovalBanner
                        showIcon={true}
                        className="utility-bill-required-for-final-approval-banner"
                      />
                    )}
                    {showUtilityBillPastDueBalanceBanner && (
                      <StyledUtilityBillPastDueBalanceBanner
                        utilityCompany={utilityCompany ?? ""}
                        utilityPastDueBalance={utilityPastDueBalance ?? 0}
                      />
                    )}
                  </>
                )
              }
            />
          ))}
        {!isAttachmentsRefreshing && (
          <DocumentUploadWithModal
            isMobile={isMobile}
            uploadedFiles={
              checkoutAttachments?.filter(
                (doc) => doc.documentType === "Other"
              ) ?? []
            }
            immediateUpload={immediateUpload}
            documentType="Other"
            description="Upload any other documents that may be required for Sunrun Approval."
            title="Other Documents"
            bannerContent={
              <>
                <ContractReviewBanners
                  messages={contractReviewMessages}
                  filter={isTrustAgreementNameMatchMessage}
                  category="customer_name_check"
                  sessionDismissScopeId={proposalId}
                />
                <ContractReviewBanners
                  messages={contractReviewMessages}
                  filter={isMobileOrManufacturedHomeMessage}
                  category="home_type_check"
                  sessionDismissScopeId={proposalId}
                />
              </>
            }
          />
        )}
      </CardsContainer>
    </>
  );
};

const LoadingWrapper = styled.div`
  margin-top: 32px;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 24px;
`;

const UtilityInfoFields = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  ${({ isMobile }) =>
    isMobile &&
    `
    flex-direction: column;
  `}
`;

const UtilityInfoField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 16px;
`;

const StyledUtilityBillRequiredForFinalApprovalBanner = styled(
  UtilityBillRequiredForFinalApprovalBanner
)`
  margin-bottom: 24px;
`;

const StyledUtilityBillPastDueBalanceBanner = styled(
  UtilityBillPastDueBalanceBanner
)`
  margin-bottom: 24px;
`;

export { ConfirmUtilityTaskView };
